import { useState, useEffect, useRef, useMemo, Component, type ReactNode } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { AppLayout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useGetMySessions, useGetMySlotWaitlist, useLeaveSlotWaitlist, useGetMyMembership } from "@workspace/api-client-react";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { format } from "date-fns";
import { Calendar, Clock, MapPin, Video, PlayCircle, Dumbbell, Bell, X, Download, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { OnboardingAgent } from "@/components/onboarding-agent";

class DashboardErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <AppLayout>
          <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
            <p className="text-white font-medium">Something went wrong loading your dashboard.</p>
            <button
              className="px-4 py-2 rounded-lg bg-primary text-black font-semibold text-sm"
              onClick={() => this.setState({ hasError: false })}
            >
              Try again
            </button>
          </div>
        </AppLayout>
      );
    }
    return this.props.children;
  }
}

function StudentDashboardInner() {
  const { user } = useAuth();
  const { toast } = useToast();
  const toastShown = useRef(false);
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);

  useEffect(() => {
    if (toastShown.current) return;
    const params = new URLSearchParams(window.location.search);

    if (params.get("session") === "paid") {
      toastShown.current = true;
      toast({ title: "Payment confirmed", description: "Your session has been booked and paid." });
    } else if (params.get("purchased")) {
      toastShown.current = true;
      toast({ title: "Purchase complete", description: "Your package has been activated." });
    } else if (params.get("checkout") === "success") {
      toastShown.current = true;
      toast({ title: "Membership activated", description: "Your membership is now active." });
    }

    if (toastShown.current) {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [toast]);
  const showOnboarding = user?.role === "STUDENT" && !user?.onboardingCompleted && !onboardingDismissed;
  
  const { data: gymPolicy } = useQuery<{ cancellationWindowHours: number; noRefundWindowHrs: number; partialCreditWindowHrs: number }>({
    queryKey: ["student", "gym-policy"],
    queryFn: () => apiFetch("/student/gym/policy"),
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  const { data: membership } = useGetMyMembership({ query: { enabled: !!user } });
  const membershipWithPlan = membership as typeof membership & { planPrivateCredits?: number };

  const { data: sessions, isLoading: sessionsLoading } = useGetMySessions({
    status: "CONFIRMED"
  });

  const upcomingSessions = useMemo(
    () => (sessions || []).filter(s => new Date(s.proposedStart) > new Date()),
    [sessions]
  );

  const { data: waitlistEntries, isLoading: waitlistLoading, refetch: refetchWaitlist } = useGetMySlotWaitlist({ query: { enabled: !!user } });
  const leaveWaitlistMutation = useLeaveSlotWaitlist();

  const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const formatTime = (t: string | null | undefined) => {
    if (!t) return "Time TBD";
    const parts = t.split(":");
    const h = parseInt(parts[0]);
    const m = parts[1] ? parseInt(parts[1]) : 0;
    if (isNaN(h)) return "Time TBD";
    const period = h >= 12 ? "PM" : "AM";
    const displayHour = h % 12 === 0 ? 12 : h % 12;
    const displayMin = `:${String(isNaN(m) ? 0 : m).padStart(2, "0")}`;
    return `${displayHour}${displayMin} ${period}`;
  };

  if (showOnboarding) {
    return <OnboardingAgent onComplete={() => setOnboardingDismissed(true)} />;
  }

  return (
    <AppLayout>
      <div className="flex flex-col gap-8 pb-12">
        
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-4xl font-display font-bold text-white mb-2">Welcome Back, {user?.firstName}</h1>
            <p className="text-muted-foreground text-lg">Ready for your next session?</p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="border-white/10 hover:bg-white/5 h-12 px-6 rounded-xl"
              onClick={() => {
                const token = localStorage.getItem("token");
                fetch(`${import.meta.env.VITE_API_URL || ""}/api/calendar/export.ics`, {
                  headers: token ? { Authorization: `Bearer ${token}` } : {},
                })
                  .then((res) => res.blob())
                  .then((blob) => {
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "openmat-sessions.ics";
                    a.click();
                    URL.revokeObjectURL(url);
                  });
              }}
            >
              <Download className="w-4 h-4 mr-2" /> Export Calendar
            </Button>

            <Link href="/sessions">
              <Button variant="outline" className="border-white/10 hover:bg-white/5 h-12 px-6 rounded-xl">My Sessions</Button>
            </Link>
            <Link href="/master-calendar">
              <Button variant="outline" className="border-white/10 hover:bg-white/5 h-12 px-6 rounded-xl">Gym Calendar</Button>
            </Link>
            <Link href="/find-trainer">
              <Button variant="outline" className="border-primary/50 text-primary hover:bg-primary hover:text-black h-12 px-6 rounded-xl">Find a Trainer</Button>
            </Link>
            <Link href="/book">
              <Button className="bg-primary text-black font-bold h-12 px-6 rounded-xl shadow-lg shadow-primary/20">Book Private</Button>
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Credits card — wired to live membership */}
          <Card className="glass-panel border-white/5 bg-gradient-to-br from-card to-card/50">
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Private Credits</CardDescription>
              <CardTitle className="text-4xl font-mono text-white">
                {membership ? membership.privateCreditsRemaining : "—"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {membership && membership.status === "ACTIVE" ? (() => {
                const total = membershipWithPlan?.planPrivateCredits || 0;
                const remaining = membership.privateCreditsRemaining;
                const used = Math.max(0, total - remaining);
                const pct = total > 0 ? Math.min(100, Math.round((remaining / total) * 100)) : 0;
                const resetDate = new Date(membership.creditsResetAt);
                const daysUntilReset = Math.max(0, Math.ceil((resetDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
                const threshold = (membershipWithPlan as any)?.lowCreditThreshold ?? 1;
                const isLow = remaining <= threshold;
                return (
                  <>
                    <div className="w-full bg-white/5 h-2 rounded-full overflow-hidden mt-2">
                      <div
                        className={`h-full rounded-full transition-all ${isLow ? "bg-amber-500" : "bg-primary"}`}
                        style={{ width: `${total > 0 ? pct : 0}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs text-muted-foreground">
                        {daysUntilReset === 0 ? "Resets today" : `Renews in ${daysUntilReset} day${daysUntilReset !== 1 ? "s" : ""}`}
                      </p>
                      {total > 0 && (
                        <p className="text-xs text-muted-foreground">
                          <span className="text-white font-medium">{used}</span> of {total} used
                        </p>
                      )}
                    </div>
                    {isLow && (
                      <div className="mt-3 flex items-center justify-between rounded-md bg-amber-500/10 border border-amber-500/20 px-3 py-2">
                        <p className="text-xs text-amber-400 font-medium">
                          {remaining === 0 ? "You're out of credits" : "Only 1 credit left"}
                        </p>
                        <Link href="/memberships">
                          <span className="text-xs text-amber-400 hover:text-amber-300 underline underline-offset-2 cursor-pointer font-medium">
                            {remaining === 0 ? "Renew now →" : "Top up →"}
                          </span>
                        </Link>
                      </div>
                    )}
                  </>
                );
              })() : (
                <p className="text-xs text-muted-foreground mt-2">
                  {membership ? "No active membership" : "Loading…"}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Next session card — wired to live sessions */}
          <Card className="glass-panel border-white/5 bg-gradient-to-br from-card to-card/50">
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Next Session</CardDescription>
              {sessionsLoading ? (
                <div className="h-7 w-40 bg-white/5 rounded animate-pulse mt-1" />
              ) : upcomingSessions.length > 0 ? (
                <CardTitle className="text-xl font-bold text-white">
                  {format(new Date(upcomingSessions[0].proposedStart), "MMM d")} at {format(new Date(upcomingSessions[0].proposedStart), "h:mm a")}
                </CardTitle>
              ) : (
                <CardTitle className="text-xl font-bold text-white/40">No upcoming sessions</CardTitle>
              )}
            </CardHeader>
            <CardContent>
              {sessionsLoading ? (
                <div className="h-4 w-24 bg-white/5 rounded animate-pulse mt-2" />
              ) : upcomingSessions.length > 0 ? (
                upcomingSessions[0].mode === "VIDEO" ? (
                  <p className="text-sm text-primary mt-2 flex items-center gap-1 font-medium">
                    <Video className="w-4 h-4" /> Video Call
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1">
                    <MapPin className="w-4 h-4" /> {upcomingSessions[0].location?.name || "In Person"}
                  </p>
                )
              ) : (
                <Link href="/book">
                  <span className="text-xs text-primary hover:underline cursor-pointer mt-2 block">Book a private →</span>
                </Link>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between border-b border-white/10 pb-4">
            <h2 className="text-2xl font-display font-bold text-white">Upcoming Privates</h2>
            <Link href="/sessions"><span className="text-sm text-primary hover:underline cursor-pointer">View All</span></Link>
          </div>
          
          {sessionsLoading ? (
            <div className="animate-pulse space-y-4">
              {[1,2].map(i => <div key={i} className="h-32 bg-white/5 rounded-2xl" />)}
            </div>
          ) : upcomingSessions.length > 0 ? (
            <div className="space-y-4">
              {upcomingSessions.slice(0, 3).map(session => (
                <div key={session.id} className="p-5 rounded-2xl bg-card border border-white/5 hover:border-primary/30 transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center text-xl font-bold text-white shrink-0 overflow-hidden">
                      {session.trainer?.avatarUrl ? <img src={session.trainer.avatarUrl} alt="" className="w-full h-full object-cover"/> : session.trainer?.firstName[0]}
                    </div>
                    <div>
                      <h4 className="font-bold text-white text-lg">{session.trainer?.firstName} {session.trainer?.lastName}</h4>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                        <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5"/> {format(new Date(session.proposedStart), "MMM d")}</span>
                        <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5"/> {format(new Date(session.proposedStart), "h:mm a")}</span>
                      </div>
                    </div>
                  </div>
                  {session.mode === 'VIDEO' ? (
                    <Button className="w-full sm:w-auto bg-blue-500 hover:bg-blue-600 text-white gap-2 font-bold shadow-lg shadow-blue-500/20">
                      <PlayCircle className="w-4 h-4"/> Join Room
                    </Button>
                  ) : (
                    <div className="px-3 py-1.5 rounded-lg bg-white/5 text-xs text-muted-foreground flex items-center gap-1 border border-white/10 w-fit">
                      <MapPin className="w-3 h-3"/> {session.location?.name || "In Person"}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center rounded-2xl border border-dashed border-white/10 bg-white/5">
              <Dumbbell className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-50" />
              <p className="text-white font-medium mb-1">No upcoming sessions</p>
              <p className="text-sm text-muted-foreground mb-4">Book a private to fast-track your progress.</p>
              <Link href="/book">
                <Button variant="outline" className="border-primary/50 text-primary hover:bg-primary hover:text-black">Find a Trainer</Button>
              </Link>
            </div>
          )}
        </div>

        {gymPolicy && gymPolicy.cancellationWindowHours > 0 && (
          <div className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3">
            <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
            <p className="text-sm text-muted-foreground">
              <span className="text-white font-medium">Cancellation policy: </span>
              Cancel at least <span className="text-white font-medium">{gymPolicy.cancellationWindowHours} hours</span> before your session to avoid a penalty fee.
              {gymPolicy.noRefundWindowHrs > 0 && (
                <> Sessions cancelled within <span className="text-white font-medium">{gymPolicy.noRefundWindowHrs}h</span> are non-refundable.</>
              )}
            </p>
          </div>
        )}

        {waitlistEntries && waitlistEntries.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <h2 className="text-2xl font-display font-bold text-white flex items-center gap-2">
                <Bell className="w-5 h-5 text-primary" />
                My Waitlist
              </h2>
            </div>
            <div className="space-y-3">
              {waitlistEntries.map((entry) => (
                <div
                  key={entry.id}
                  className="p-4 rounded-2xl bg-card border border-white/5 hover:border-primary/30 transition-all flex items-center justify-between gap-4"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center text-base font-bold text-white shrink-0 overflow-hidden">
                      {entry.trainer?.avatarUrl ? (
                        <img src={entry.trainer.avatarUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        entry.trainer?.firstName?.[0] || "?"
                      )}
                    </div>
                    <div>
                      <p className="text-white font-medium">
                        {entry.trainer?.firstName} {entry.trainer?.lastName}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {entry.dayOfWeek != null && entry.timeSlot
                          ? `${dayNames[entry.dayOfWeek]} at ${formatTime(entry.timeSlot)}`
                          : entry.dayOfWeek != null
                          ? `${dayNames[entry.dayOfWeek]} (time TBD)`
                          : entry.timeSlot
                          ? `Time: ${formatTime(entry.timeSlot)} (day TBD)`
                          : "Waitlisted"}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground hover:text-red-400 hover:bg-red-400/10"
                    onClick={async () => {
                      try {
                        await leaveWaitlistMutation.mutateAsync({
                          trainerId: entry.trainerId,
                          entryId: entry.id,
                        });
                        refetchWaitlist();
                      } catch {}
                    }}
                    disabled={leaveWaitlistMutation.isPending}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </AppLayout>
  );
}

export default function StudentDashboard() {
  return (
    <DashboardErrorBoundary>
      <StudentDashboardInner />
    </DashboardErrorBoundary>
  );
}
